import React, {
    useEffect,
    useState,
} from "react";

import {
    Flex,
    Box,
    Text,
} from "@chakra-ui/react";

import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

import {
    EllipsisVertical,
    Eye,
    Pencil,
    Users,
    FileText,
    Codesandbox,
    Download,
    ImageDown,
} from "lucide-react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import ReusableTable from "components/Tables/ReusableTable";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import ReusableDrawer from "components/Drawers/ReusableDrawer";

import GradientButton from "components/Buttons/GradientButton";

import ViewDriverPartner from "./components/ViewDriverPartner";
import EditDriverPartnerDrawer from "./components/EditDriverPartnerDrawer";
import ReferredDriversDrawer from "./components/ReferredDriversDrawer";

import AddContactLogDrawer from "../ContactLogManagement/components/AddContactLogDrawer";
import ViewContactLogsDrawer from "../ContactLogManagement/components/ViewContactLogsDrawer";

import {
    createContactLogAction,
    fetchUserContactLogsAction,
} from "state/actions/contactLogActions";

import {
    useCustomNotification,
} from "components/Toasts/OpenNotification";

import {
    fetchDriverPartnersAction,
} from "state/actions/driverPartnerActions";

import { formatDate } from "utils/formatDate";
import { generateDriverPartnerPoster } from "./components/GenerateDriverPartnerPoster";

const DriverPartners = () => {

    const dispatch = useDispatch();

    const notify =
        useCustomNotification();
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;


    /* ===================================================== */
    /* DRIVER PARTNERS STATE */
    /* ===================================================== */

    const {
        driverPartners,
        loading,
        error,
        total,
        hasMore,
    } = useSelector(
        (state) =>
            state.driverPartners
    );


    /* ===================================================== */
    /* CONTACT LOGS */
    /* ===================================================== */

    const {
        logs: contactLogs,
        loading:
            contactLogsLoading,
        error:
            contactLogsError,
    } = useSelector(
        (state) =>
            state.contactLogs
    );


    /* ===================================================== */
    /* CONTACT STATUS */
    /* ===================================================== */

    const [
        contactStatuses,
        setContactStatuses,
    ] = useState({});


    useEffect(() => {

        const loadContactStatuses =
            async () => {

                if (
                    !driverPartners?.length
                ) {

                    setContactStatuses(
                        {}
                    );

                    return;
                }


                const statuses =
                    {};


                await Promise.all(
                    driverPartners.map(
                        async (
                            partner
                        ) => {

                            const userId =
                                partner
                                    ?.user
                                    ?.user_id;


                            if (
                                !userId
                            ) {
                                return;
                            }


                            const response =
                                await dispatch(
                                    fetchUserContactLogsAction(
                                        userId
                                    )
                                );


                            if (
                                response?.success &&
                                Array.isArray(
                                    response.data
                                ) &&
                                response.data.length
                            ) {

                                const latestLog =
                                    [
                                        ...response.data,
                                    ].sort(
                                        (
                                            a,
                                            b
                                        ) =>
                                            new Date(
                                                b.contacted_at
                                            ) -
                                            new Date(
                                                a.contacted_at
                                            )
                                    )[0];


                                statuses[
                                    userId
                                ] =
                                    latestLog?.status ||
                                    "Not Contacted";

                            } else {

                                statuses[
                                    userId
                                ] =
                                    "Not Contacted";
                            }

                        }
                    )
                );


                setContactStatuses(
                    statuses
                );
            };


        loadContactStatuses();

    }, [
        driverPartners,
        dispatch,
    ]);


    /* ===================================================== */
    /* PAGINATION */
    /* ===================================================== */

    const [
        currentPage,
        setCurrentPage,
    ] = useState(1);

    const PAGE_SIZE = 20;


    /* ===================================================== */
    /* SEARCH */
    /* ===================================================== */

    const [
        searchField,
        setSearchField,
    ] = useState(null);

    const [
        searchValue,
        setSearchValue,
    ] = useState("");

    const [
        searchTerm,
        setSearchTerm,
    ] = useState("");


    /* ===================================================== */
    /* SELECTED PARTNER */
    /* ===================================================== */

    const [
        selectedPartner,
        setSelectedPartner,
    ] = useState(null);


    /* ===================================================== */
    /* DRAWERS */
    /* ===================================================== */

    const [
        isViewOpen,
        setViewOpen,
    ] = useState(false);

    const [
        isEditOpen,
        setEditOpen,
    ] = useState(false);

    const [
        isDriversOpen,
        setDriversOpen,
    ] = useState(false);

    const [
        isAddContactLogOpen,
        setAddContactLogOpen,
    ] = useState(false);

    const [
        selectedUserForLog,
        setSelectedUserForLog,
    ] = useState(null);

    const [
        isContactLogsOpen,
        setContactLogsOpen,
    ] = useState(false);

    const [
        contactLogsUser,
        setContactLogsUser,
    ] = useState(null);


    /* ===================================================== */
    /* SEARCH */
    /* ===================================================== */

    const handleSearch = (
        field,
        value
    ) => {

        setCurrentPage(1);

        setSearchField(
            field
        );

        setSearchTerm(
            value
        );
    };


    useEffect(() => {

        const timer =
            setTimeout(() => {

                setSearchValue(
                    searchTerm
                );

            }, 500);


        return () =>
            clearTimeout(
                timer
            );

    }, [
        searchTerm,
    ]);


    /* ===================================================== */
    /* FETCH DRIVER PARTNERS */
    /* ===================================================== */

    useEffect(() => {

        dispatch(
            fetchDriverPartnersAction(
                currentPage,
                PAGE_SIZE,
                searchField,
                searchValue
            )
        );

    }, [
        dispatch,
        currentPage,
        searchField,
        searchValue,
    ]);


    /* ===================================================== */
    /* ERROR */
    /* ===================================================== */

    useEffect(() => {

        if (error) {

            notify({
                type: "error",
                message:
                    "Failed to load Driver Partners",
                description:
                    error,
            });

        }

    }, [
        error,
        notify,
    ]);


    useEffect(() => {

        if (
            contactLogsError
        ) {

            notify({
                type: "error",
                message:
                    "Failed to load contact logs",
                description:
                    contactLogsError,
            });

        }

    }, [
        contactLogsError,
        notify,
    ]);


    /* ===================================================== */
    /* VIEW PARTNER */
    /* ===================================================== */

    const handleView = (
        partner
    ) => {

        setSelectedPartner(
            partner
        );

        setViewOpen(
            true
        );
    };


    /* ===================================================== */
    /* EDIT PARTNER */
    /* ===================================================== */

    const handleEdit = (
        partner
    ) => {

        setSelectedPartner(
            partner
        );

        setEditOpen(
            true
        );
    };


    /* ===================================================== */
    /* VIEW REFERRED DRIVERS */
    /* ===================================================== */

    const handleViewDrivers = (
        partner
    ) => {

        setSelectedPartner(
            partner
        );

        setDriversOpen(
            true
        );
    };


    /* ===================================================== */
    /* ADD CONTACT LOG */
    /* ===================================================== */

    const handleOpenContactLog = (
        partner
    ) => {

        setSelectedUserForLog(
            partner?.user ||
            null
        );

        setAddContactLogOpen(
            true
        );
    };


    const handleCloseContactLog =
        () => {

            setAddContactLogOpen(
                false
            );

            setSelectedUserForLog(
                null
            );
        };


    const handleAddContactLog =
        async (
            data
        ) => {

            try {

                const response =
                    await dispatch(
                        createContactLogAction(
                            data
                        )
                    );


                if (
                    response?.success
                ) {

                    notify({
                        type: "success",
                        message:
                            "Contact log created successfully",
                    });


                    handleCloseContactLog();


                    if (
                        contactLogsUser
                            ?.user_id
                    ) {

                        dispatch(
                            fetchUserContactLogsAction(
                                contactLogsUser.user_id
                            )
                        );
                    }

                } else {

                    notify({
                        type: "error",
                        message:
                            "Failed to create contact log",
                        description:
                            response?.error ||
                            "Unable to create contact log.",
                    });
                }

            } catch (
                error
            ) {

                notify({
                    type: "error",
                    message:
                        "Failed to create contact log",
                    description:
                        error?.message ||
                        "Unable to create contact log.",
                });
            }
        };


    /* ===================================================== */
    /* VIEW CONTACT LOGS */
    /* ===================================================== */

    const handleViewContactLogs =
        async (
            partner
        ) => {

            const user =
                partner?.user;

            const userId =
                user?.user_id;


            if (!userId) {

                notify({
                    type: "error",
                    message:
                        "Unable to load contact logs",
                    description:
                        "User ID is missing for this Driver Partner.",
                });

                return;
            }


            setContactLogsUser(
                user
            );

            setContactLogsOpen(
                true
            );


            await dispatch(
                fetchUserContactLogsAction(
                    userId
                )
            );
        };


    const handleCloseContactLogs =
        () => {

            setContactLogsOpen(
                false
            );

            setContactLogsUser(
                null
            );
        };


    /* ===================================================== */
    /* MENU */
    /* ===================================================== */

    const generateMenuItems = (
        row
    ) => [

        {
            icon: (
                <Eye
                    size={16}
                />
            ),

            text:
                "View Partner",

            onClick: () =>
                handleView(
                    row.original
                ),
        },


        {
            icon: (
                <Users
                    size={16}
                />
            ),

            text:
                "View Recruited Drivers",

            onClick: () =>
                handleViewDrivers(
                    row.original
                ),
        },
 {
    icon: (
        <ImageDown
            size={16}
        />
    ),

    text: "Generate Poster",

    onClick: async () => {
        try {
            const partner =
                row.original;

            await generateDriverPartnerPoster({
                user: partner?.user,
                driverPartner:
                    partner?.driver_partner,
                mediaBaseUrl: BASE_URL,
            });

            notify({
                type: "success",
                message:
                    "Poster downloaded successfully",
            });

        } catch (error) {
            console.error(
                "Failed to generate Driver Partner poster:",
                error
            );

            notify({
                type: "error",
                message:
                    "Failed to generate poster",
                description:
                    error?.message ||
                    "Unable to generate Driver Partner poster.",
            });
        }
    },
},

        {
            icon: (
                <FileText
                    size={16}
                />
            ),

            text:
                "Add Contact Log",

            onClick: () =>
                handleOpenContactLog(
                    row.original
                ),
        },


        {
            icon: (
                <FileText
                    size={16}
                />
            ),

            text:
                "View Contact Logs",

            onClick: () =>
                handleViewContactLogs(
                    row.original
                ),
        },


        {
            icon: (
                <Pencil
                    size={16}
                />
            ),

            text:
                "Update Status",

            onClick: () =>
                handleEdit(
                    row.original
                ),
        },

    ];


    /* ===================================================== */
    /* COLUMNS */
    /* ===================================================== */

    const columnsData = [

        {
            Header:
                "Partner Name",

            accessor:
                (row) =>
                    `${row.user?.first_name || ""} ${
                        row.user?.last_name || ""
                    }`.trim(),

            filterField:
                "partner_name",
        },


        {
            Header:
                "Email",

            accessor:
                (row) =>
                    row.user?.email ||
                    "N/A",

            filterField:
                "email",
        },


        {
            Header:
                "Phone",

            accessor:
                (row) =>
                    row.user
                        ?.phone_number ||
                    "N/A",

            filterField:
                "phone_number",
        },


        {
            Header:
                "Contact Status",

            accessor:
                (row) =>
                    contactStatuses[
                        row.user?.user_id
                    ] ||
                    "Not Contacted",

            Cell:
                ({
                    value,
                }) => (

                    <StatusBadge
                        label={
                            value
                        }

                        variant={
                            value ===
                            "Not Contacted"
                                ? "gray"
                                : getStatusVariant(
                                      value
                                  )
                        }
                    />

                ),
        },


        {
            Header:
                "Referral Code",

            accessor:
                (row) =>
                    row.driver_partner
                        ?.partner_referral_code ||
                    "N/A",

            filterField:
                "partner_referral_code",
        },


        {
            Header:
                "Drivers",

            accessor:
                (row) =>
                    row.stats
                        ?.total_referred_drivers ||
                    0,
        },


        {
            Header:
                "Status",

            accessor:
                (row) =>
                    row.driver_partner
                        ?.status ||
                    "Unknown",

            filterField:
                "status",

            Cell:
                ({
                    value,
                }) => (

                    <StatusBadge
                        label={
                            value
                        }

                        variant={
                            getStatusVariant(
                                value
                            )
                        }
                    />

                ),
        },


        {
            Header:
                "Created",

            accessor:
                (row) =>
                    row.driver_partner
                        ?.created_at,

            Cell:
                ({
                    value,
                }) =>
                    value
                        ? formatDate(
                              value
                          )
                        : "N/A",
        },


        {
            Header:
                "Actions",

            id:
                "actions",

            Cell:
                ({
                    row,
                }) => (

                    <ThreeDotMenu
                        icon={
                            <EllipsisVertical
                                size={20}
                                color="grey"
                            />
                        }

                        items={
                            generateMenuItems(
                                row
                            )
                        }
                    />

                ),
        },

    ];


    /* ===================================================== */
    /* RENDER */
    /* ===================================================== */

    return (

        <Flex
            flexDirection="column"
            pt={{
                base: "120px",
                md: "25px",
            }}
        >

            {/* HEADER */}

            <Box p={6}>

                <Flex
                    justify="space-between"
                >

                    <Text
                        fontSize="md"
                        fontWeight="bold"
                    >

                        {loading ? (

                            <Skeleton
                                width="150px"
                            />

                        ) : (

                            `Driver Partners: ${
                                total || 0
                            }`

                        )}

                    </Text>

                </Flex>

            </Box>


            {/* TABLE */}

            <ReusableTable

                columnsData={
                    columnsData
                }

                tableData={
                    Array.isArray(
                        driverPartners
                    )
                        ? driverPartners
                        : []
                }

                isLoading={
                    loading
                }

                onSearch={
                    handleSearch
                }
            />


            {/* ================================================= */}
            {/* PAGINATION */}
            {/* Same Previous / Page / Next pattern */}
            {/* ================================================= */}

            <Box
                mt={4}
                display="flex"
                justifyContent="center"
                gap={2}
            >

                <GradientButton
                    onClick={() =>
                        setCurrentPage(
                            (prev) =>
                                Math.max(
                                    prev - 1,
                                    1
                                )
                        )
                    }

                    disabled={
                        currentPage ===
                        1
                    }

                    height="30px"
                >
                    Previous
                </GradientButton>


                <Text
                    alignSelf="center"
                >
                    Page{" "}
                    {
                        currentPage
                    }
                </Text>


                <GradientButton
                    onClick={() =>
                        setCurrentPage(
                            (prev) =>
                                prev + 1
                        )
                    }

                    disabled={
                        !hasMore
                    }

                    height="30px"
                >
                    Next
                </GradientButton>

            </Box>


            {/* ================================================= */}
            {/* VIEW PARTNER */}
            {/* ================================================= */}

            <ReusableDrawer

                isOpen={
                    isViewOpen
                }

                onClose={() =>
                    setViewOpen(
                        false
                    )
                }

                headerContent={
                    <h2>
                        Driver Partner
                    </h2>
                }

                bodyContent={

                    selectedPartner && (

                        <ViewDriverPartner

                            partner={
                                selectedPartner
                            }

                            onClose={() =>
                                setViewOpen(
                                    false
                                )
                            }

                        />

                    )

                }

            />


            {/* ================================================= */}
            {/* EDIT PARTNER */}
            {/* ================================================= */}

            <EditDriverPartnerDrawer

                isOpen={
                    isEditOpen
                }

                onClose={() =>
                    setEditOpen(
                        false
                    )
                }

                partner={
                    selectedPartner
                }

            />


            {/* ================================================= */}
            {/* REFERRED DRIVERS */}
            {/* ================================================= */}

            <ReferredDriversDrawer

                isOpen={
                    isDriversOpen
                }

                onClose={() =>
                    setDriversOpen(
                        false
                    )
                }

                referralCode={
                    selectedPartner
                        ?.driver_partner
                        ?.partner_referral_code
                }

            />


            {/* ================================================= */}
            {/* ADD CONTACT LOG */}
            {/* ================================================= */}

            <AddContactLogDrawer

                isOpen={
                    isAddContactLogOpen
                }

                onClose={
                    handleCloseContactLog
                }

                onAdd={
                    handleAddContactLog
                }

                user={
                    selectedUserForLog
                }

            />


            {/* ================================================= */}
            {/* VIEW CONTACT LOGS */}
            {/* ================================================= */}

            <ViewContactLogsDrawer

                isOpen={
                    isContactLogsOpen
                }

                onClose={
                    handleCloseContactLogs
                }

                logs={
                    contactLogs
                }

                user={
                    contactLogsUser
                }

                loading={
                    contactLogsLoading
                }

            />

        </Flex>
    );
};


export default DriverPartners;